/**
 * Editing plot blocks in a finished draft.
 *
 * Same contract as `anim-splice.ts`: the server repairs a plot after the prose
 * has already streamed, and both the server (before persisting) and the client
 * (mid-stream) have to make the identical edit to the identical text.
 *
 * Blocks are keyed by their original body rather than by ordinal. Ordinals
 * shift the moment one block is dropped, and the whole point of this pass is
 * that some blocks get dropped.
 */

const PLOT_FENCE = /^([ \t]*)(`{3,}|~{3,})[ \t]*plot[^\n]*\n([\s\S]*?)(\n[ \t]*\2)/gm;

/** Every plot block in a draft, in reading order, as written. */
export function findPlotBlocks(content: string): readonly string[] {
  const found: string[] = [];

  PLOT_FENCE.lastIndex = 0;
  let match = PLOT_FENCE.exec(content);

  while (match !== null) {
    found.push(match[3] ?? "");
    match = PLOT_FENCE.exec(content);
  }

  return found;
}

/**
 * Swap one plot block's body for a repaired one, or remove it when `fixed` is
 * null. A no-op when no block matches, which is the case on a stream that was
 * aborted before the fence closed.
 */
export function replacePlotBlock(
  content: string,
  original: string,
  fixed: string | null,
): string {
  PLOT_FENCE.lastIndex = 0;

  return content.replace(
    PLOT_FENCE,
    (whole, indent: string, fence: string, body: string, close: string) => {
      if (normalize(body) !== normalize(original)) return whole;
      if (fixed === null) return "";

      const json = fixed
        .trim()
        .split("\n")
        .map((line) => (line.length > 0 ? indent + line : line))
        .join("\n");

      return `${indent}${fence}plot\n${json}${close}`;
    },
  );
}

/**
 * Indentation and trailing whitespace differ between the raw draft and what a
 * round-trip through the model returns, so blocks are matched on a stripped
 * form — the same normalisation `figures.ts` uses for numbering.
 */
function normalize(source: string): string {
  return source
    .split("\n")
    .map((line) => line.trim())
    .join("\n")
    .trim();
}
